import { FunctionComponent } from 'react';
import { CommentPage } from './Pages/CommentPage';
import { RatingPage } from './Pages/RatingPage';
import { FollowUpQuestionsPage } from './Pages/FollowUpQuestionsPage';
import { OutroPage } from './Pages/OutroPage';
import { CampaignClosedPage } from './Pages/CampaignClosedPage';
import { AlreadyAnsweredPage } from './Pages/AlreadyAnsweredPage';
import { UnsubscribedPage } from './Pages/UnsubscribedPage';
import { ResponseLimitReachedPage } from './Pages/ResponseLimitReachedPage';
import type { SurveyTypeValue } from '../Data/SurveyType';
import type { LegacyResponse, Respondent, Template } from '../types';

interface ContentProps {
  notRated?: boolean;
  allowPreviousButton?: boolean;
  allowRatingChange?: boolean;
  sending?: boolean;
  finished?: boolean;
  newSection?: boolean;
  questionFirst?: boolean;
  unsubscribed?: boolean;
  responseLimitReached?: boolean;
  template: Template;
  response: LegacyResponse;
  respondent: Respondent;
  rate: (rating: number) => void;
  surveyType: SurveyTypeValue;
  toggleNextSection: () => void;
  togglePreviousSection: () => void;
  updateResponse: (key: string, value: unknown) => void;
  setMultipleChoice: (
    e: React.MouseEvent<HTMLInputElement>,
    answerKey: string,
    choice: string
  ) => void;
  sendFinalResponse: (onSuccess?: () => void) => void;
  showCategoryRequiredMessage?: boolean;
  setIsRatingPageRendered: (value: boolean) => void;
}

export const Content: FunctionComponent<ContentProps> = ({
  notRated,
  allowPreviousButton,
  allowRatingChange,
  sending,
  finished,
  newSection,
  questionFirst,
  unsubscribed,
  responseLimitReached,
  template,
  response,
  respondent,
  rate,
  surveyType,
  toggleNextSection,
  togglePreviousSection,
  updateResponse,
  setMultipleChoice,
  sendFinalResponse,
  showCategoryRequiredMessage,
  setIsRatingPageRendered
}) => {
  const ratings = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10];
  const showCampaignClosedPage = !(respondent?.campaign?.acceptingResponses ?? true);
  const showAlreadyAnsweredPage = respondent?.response?.closed ?? false;

  const ratingPageElement = (
    <RatingPage
      notRated={notRated}
      rating={response.rating}
      ratings={ratings}
      template={template}
      rate={rate}
      sending={sending}
      setIsRatingPageRendered={setIsRatingPageRendered}
    />
  );

  const followUpQuestionsPageElement = (
    <FollowUpQuestionsPage
      ratings={ratings}
      template={template}
      response={response}
      updateResponse={updateResponse}
      setMultipleChoice={setMultipleChoice}
      toggleNextSection={toggleNextSection}
      sendFinalResponse={sendFinalResponse}
      sending={sending}
      questionFirst={questionFirst}
      togglePreviousSection={togglePreviousSection}
      allowPreviousButton={allowPreviousButton}
    />
  );

  const outroPageElement = (
    <OutroPage
      template={template}
      response={response}
      updateResponse={updateResponse}
      sendFinalResponse={sendFinalResponse}
      sending={sending}
    />
  );

  const commentPageElement = (
    <CommentPage
      allowRatingChange={allowRatingChange}
      toggleNextSection={toggleNextSection}
      template={template}
      response={response}
      updateResponse={updateResponse}
      sendFinalResponse={sendFinalResponse}
      surveyType={surveyType}
      showCategoryRequiredMessage={showCategoryRequiredMessage}
      sending={sending}
      questionFirst={questionFirst}
      ratingPage={ratingPageElement}
    />
  );

  const unsubscribedPageElement = <UnsubscribedPage template={template} />;

  const getNormalSequenceAfterRating = () => {
    if (finished) {
      return outroPageElement;
    }
    if (newSection) {
      return followUpQuestionsPageElement;
    }
    return commentPageElement;
  };

  const getSurveyContent = () => {
    if (unsubscribed) {
      return unsubscribedPageElement;
    }
    if (responseLimitReached) {
      return <ResponseLimitReachedPage template={template} />;
    }
    if (showCampaignClosedPage) {
      return <CampaignClosedPage template={template} />;
    }
    if (showAlreadyAnsweredPage) {
      return <AlreadyAnsweredPage template={template} />;
    }

    if (questionFirst) {
      // inverse survey sequence: extra questions first, then rate, then comment, then outro page.
      return (
        <>
          {!newSection && !finished && followUpQuestionsPageElement}
          {notRated && newSection && ratingPageElement}
          {!notRated && (newSection ? commentPageElement : outroPageElement)}
        </>
      );
    }

    // normal survey sequence: rate first, then comment, then extra questions, then outro page.
    return (
      <>
        {notRated && ratingPageElement}
        {!notRated && getNormalSequenceAfterRating()}
      </>
    );
  };

  return <div>{getSurveyContent()}</div>;
};
